import {MapObject} from "../../Server/DB/mapObjects/@MapObject.js";
import {CheckAgeFilter, ParseSpecialFilter, SpecialFilter} from "./@Shared.js";
import {collectRasterFeatures} from "../../Terrain/Heights/Poisson/@Shared/ConstraintRasterizer.js";
import {CollectMaterialContextRelevantObjects, CollectSceneryRelevantObjects, CollectTerrainRelevantObjects} from "../../Terrain/Heights/TerrainExporter.js";
import {PackageUpMapObjForTerrain} from "../../Terrain/Heights/Poisson/@Shared/ConstraintRasterizer/MapObject_ForTerrain.js";

export function FilterTextToTokens(filterText: string): string[] {
	return filterText.trim().toLowerCase().split(/\s+/).filter(t=>t.length > 0);
}

export function IsFilterMeaningful(filterText: string): boolean {
	const tokens = FilterTextToTokens(filterText);
	const meaningfulTokens = tokens.filter(token=>{
		if (token.startsWith("#")) return false;
		return true;
	});
	return meaningfulTokens.length > 0;
}

/** Check whether a MapObject passes all of the filters encoded in filterText.
 *  filterText is a space-separated list of tokens; each token is either a plain word (must
 *  appear in the object name, case-insensitive) or a special `<keyword><cmp><value>` filter. */
export function DoesObjectPassFilter(obj: MapObject, filterText: string, displayTitle?: string): boolean {
	const tokens = FilterTextToTokens(filterText);
	if (tokens.length == 0) return true;

	for (const token of tokens) {
		// A leading "#" disables the token (eg. "#is:isoline" or "#coast" is skipped entirely).
		if (token.startsWith("#")) continue;
		const special = ParseSpecialFilter(token);
		if (special != null) {
			if (!DoesObjectPassSpecialFilter(obj, special)) return false;
		} else {
			// plain word: must appear somewhere in the object name or display-title
			const hasMatch = (
				obj.name.toLowerCase().includes(token) ||
				//(obj.label_textOverride != null && obj.label_textOverride.toLowerCase().includes(token)) ||
				(displayTitle != null && displayTitle.toLowerCase().includes(token))
			)
			if (!hasMatch) return false;
		}
	}
	return true;
}

function DoesObjectPassSpecialFilter(obj: MapObject, filter: SpecialFilter): boolean {
	const {keyword, comparison, value} = filter;

	if (keyword == "age") {
		return CheckAgeFilter(obj.createdAt, comparison, value);
	}

	if (keyword == "is") {
		// Boolean "is:<flag>" filters. Comparison should be ":"; other comparisons are
		// treated as ":" (these are boolean flags, not ranges).
		if (value == "isoline") {
			// "is:isoline" — the object contributes anything rasterized for terrain solving.
			// Reuses the solver's own bridge (collectRasterFeatures) so it can't drift from
			// the rasterizer's evolving activeness rules (spot-height mods, active path feats).
			if (comparison != ":") return true; // not a boolean slot
			return collectRasterFeatures([PackageUpMapObjForTerrain(obj, {}, {})]).length > 0;
		}
		// Unknown is:<flag> – don't filter out the object
		return true;
	}

	if (keyword == "has") {
		// "has:<kind>" filters — matches objects that own at least one element of the given kind,
		// including disabled ones (the raw arrays are checked, not the resolved/active subsets).
		if (value == "path") {
			if (comparison != ":") return true; // not a boolean slot
			return (obj.paths?.length ?? 0) > 0;
		}
		if (value == "mesh") {
			if (comparison != ":") return true; // not a boolean slot
			return (obj.meshes?.length ?? 0) > 0;
		}
		// Unknown has:<kind> – don't filter out the object
		return true;
	}

	if (keyword == "for") {
		// "for:<domain>" filters — broader than "is:"; matches objects that *can* affect a domain
		// whether or not they're currently enabled/active.
		if (value == "terrain") {
			// "for:terrain" — the object has any terrain-height-relevant element (TerrainDefinition/TerrainRegion
			// modifier, terrainFeature-bearing path, etc.), enabled or not. Passes the single object alone
			// to CollectTerrainRelevantObjects (whole-map mode + disabled-inclusive); length 0/1 = match.
			if (comparison != ":") return true; // not a boolean slot
			return CollectTerrainRelevantObjects([obj], {includeDisabledTerrainElements: true}).objects.length > 0;
		}
		if (value == "material") {
			// "for:material" — the object can affect the terrain's procedural material: a `terrainMaterials`
			// modifier, or any path bearing a `materialFeature`. Passes the single object alone to
			// CollectMaterialContextRelevantObjects (whole-map mode + disabled-inclusive); length 0/1 = match.
			if (comparison != ":") return true; // not a boolean slot
			return CollectMaterialContextRelevantObjects([obj], {includeDisabledMaterialElements: true}).objects.length > 0;
		}
		if (value == "scenery") {
			// "for:scenery" — the object can place procedural scenery: any path bearing a `sceneryFeature`.
			// Passes the single object alone to CollectSceneryRelevantObjects (whole-map mode +
			// disabled-inclusive); length 0/1 = match.
			if (comparison != ":") return true; // not a boolean slot
			return CollectSceneryRelevantObjects([obj], {includeDisabledSceneryElements: true}).objects.length > 0;
		}
		// Unknown for:<domain> – don't filter out the object
		return true;
	}

	// unknown keyword – don't filter out the object
	return true;
}